import React, {useCallback, useEffect, useRef, useState} from 'react';

import {Document, Page, pdfjs} from 'react-pdf';
import {Box, Paper, Typography} from '@mui/material';
import {SERVER_URL} from '../../api/url';
import {useDocumentViewer} from '../../context/DocumentViewerContext';

import BoxMessage from '../../components/design-system/box/BoxMessage';
import {ReactComponent as NotSupportIcon} from '../../assets/chat/not-support.svg';

// 워커
// pdfjs.disableWorker = true;
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

/**
 * DocumentViewer
 * @param {string}   documentUrl          PDF 파일을 렌더링하기 위한 경로 (현재는 downloadLink)
 */

// const SERVER_URL = 'https://women.dfinite.ai';

export default function DocumentContent({ documentUrl }) {
    const pageRefs = useRef([]);

    const { currentDocumentIndex, switchDocument } = useDocumentViewer();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalPages, setTotalPages] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    // 서버 주소와 파일명을 합쳐 완전한 경로 만들기
    const getCompleteUrl = useCallback((url) => {
        if (!url) return '';

        return `${SERVER_URL}/viewer/${url}`;
    }, []);

    const completeUrl = getCompleteUrl(documentUrl);

    const onLoadSuccess = ({ numPages }) => {
        setLoading(false);
        setError(null);
        setTotalPages(numPages);
    };
    
    const onLoadError = (error) => {
        console.error('[react-pdf] load error :', error);
        setLoading(false);
        setError(error?.message || 'PDF 로드 실패');
    };

    const reloadDocument = useCallback(() => {
        setError(null);
        setLoading(true);

        switchDocument(currentDocumentIndex);
    }, [currentDocumentIndex, switchDocument]);

    // 파일 확장자 검증 함수
    const validateExtension = useCallback((documentUrl) => {
        if (!documentUrl) return { isValid: false, message: '문서 URL이 존재하지 않음' };
        
        const lastDotIndex = documentUrl.lastIndexOf('.');
        if (lastDotIndex === -1) {
            return { isValid: false, message: '지원하지 않는 문서 형식', subMessage: '이 뷰어에서는 해당 형식의 문서를 열 수 없어요.' };
        }
        
        const extension = documentUrl.substring(lastDotIndex + 1).toLowerCase();
        if (extension !== 'pdf') {
            return { isValid: false, message: '지원하지 않는 문서 형식', subMessage: '이 뷰어에서는 해당 형식의 문서를 열 수 없어요.' };
        }
        
        return { isValid: true, message: '', subMessage: '' };
    }, []);

    const isValidExtension = validateExtension(documentUrl);

    useEffect(() => {
        if (!documentUrl) return;

        // 지원하지 않는 확장자인 경우, 바로 로딩 종료하고 Document 렌더 막기
        if (!isValidExtension.isValid) {
            setLoading(false);
            setError(null);
            setTotalPages(null);
            
            return;
        }

        setLoading(true);
        setError(null);
        setTotalPages(null);
    }, [documentUrl, completeUrl, isValidExtension.isValid]);

    // 스크롤 기반 현재 페이지 계산
    useEffect(() => {
        if (!totalPages) return;
        
        const thresholds = Array.from({ length: 21 }, (_, i) => i / 20);
        const visibleRatios = new Array(totalPages).fill(0);

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const idx = Number(entry.target.getAttribute('data-index'));
                visibleRatios[idx] = entry.intersectionRatio;
            });

            const maxRatio = Math.max(...visibleRatios);
            const maxIndex = visibleRatios.indexOf(maxRatio);
            
            if (maxIndex >= 0) setCurrentPage(maxIndex + 1);
        }, { root: null, rootMargin: '0px', threshold: thresholds });
        
        pageRefs.current.forEach((el) => el && observer.observe(el));
        
        return () => observer.disconnect();
    }, [totalPages]);

    return (
        <Paper
            elevation={0}
            sx={{ width: '100%', height: '100%', overflow: 'hidden', p: '10px 40px', bgcolor: '#F8F8F8', borderRadius: 0 }}
        >
            {/* 로딩 중 */}
            {loading && <BoxMessage height='100%' type='loading' />}

            {/* 에러 */}
            {!loading && error && 
                <BoxMessage
                    height='100%'
                    type='error'
                    handleReload={reloadDocument}
                />
            }

            {/* 지원하지 않는 확장자 */}
            {!loading && !error && !isValidExtension.isValid && 
                <BoxMessage
                    height='100%'
                    icon={<NotSupportIcon />}
                    message={isValidExtension.message}
                    subMessage={isValidExtension.subMessage}
                />
            }
            
            {/* 용량 초과 */}
            {!loading && !error && totalPages && totalPages > 300 &&
                <BoxMessage
                    height='100%'
                    icon={<NotSupportIcon />}
                    message='문서 크기 초과'
                    subMessage='문서 용량이 너무 커서 뷰어에서 열 수 없어요.'
                />
            }

            {/* 정상 */}
            {!error && isValidExtension.isValid &&
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {totalPages &&
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', mb: '16px' }}>
                            <Box sx={{ width: 'fit-content', height: '20px', px: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#FFF', borderRadius: '4px' }}>
                                <Typography variant='subtitle1'>{currentPage}</Typography>
                            </Box>
                            <Typography variant='subtitle1'>/ {totalPages}</Typography>
                        </Box>
                    }
                    <Box sx={{ minHeight: 0, overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <Document
                            file={completeUrl}
                            onLoadSuccess={onLoadSuccess}
                            onLoadError={onLoadError}
                            // 기본 로딩 화면 억제
                            loading={<></>}
                            // 기본 메시지 억제
                            error={<></>}
                            noData={<></>}
                        >
                        {totalPages &&
                            Array.from({ length: totalPages }, (_, i) => (
                                <div
                                    ref={(el) => (pageRefs.current[i] = el)}
                                    key={`page_wrap_${i + 1}`}
                                    data-index={i}
                                    style={{ marginBottom: 16 }}
                                >
                                    <Page
                                        key={`page_${i + 1}`}
                                        pageNumber={i + 1}
                                        renderTextLayer={false}
                                        renderAnnotationLayer={false} 
                                    />
                                </div>
                            ))
                        }
                        </Document>
                    </Box>
                </Box>
            }
        </Paper>
    );
}
